import logging from "utils/logging";
import Provider from "provider";
import {
  createCloseListenerAction,
  createOpenListenerAction,
  modelsUpdated,
} from "../../redux/actions";
import {
  createEntityAdapter,
  createSlice,
  createSelector,
} from "@reduxjs/toolkit";
import { buildModelReducer } from "../../redux/reducers";
import { gameEntity } from "../../models/game";
import { getSimpleGameStateForGame } from "../gamestate/gameStateSlice";
import {
  PUBLISH_STATUS,
  getPublishStatus,
  touchedAtMs,
} from "./lifecycle";

const log = logging.getLogger("Manager Slice"); //eslint-disable-line no-unused-vars

const managedGamesAdapter = createEntityAdapter();
const initialState = managedGamesAdapter.getInitialState({
  loadingGames: true,
});

const managedGamesSlice = createSlice({
  name: "managedGames",
  initialState,
  reducers: {
    loadingManagedGames(state) {
      state.loadingGames = true;
    },
    gamesLoaded(state, action) {
      state.loadingGames = false;
    },
  },
  extraReducers: (builder) => {
    buildModelReducer({
      builder,
      entity: gameEntity,
      loadingKey: "loadingGames",
      adapter: managedGamesAdapter,
    });
  },
});

const watchManagedGamesActionBase = createOpenListenerAction(
  "manage/watch_managed_games",
  "watch-managed-games"
);

const stopWatchingManagedGamesActionBase = createCloseListenerAction(
  "manage/stop_watching_managed_games",
  "watch-managed-games"
);

const watchManagedGamesAction = (userId) => (dispatch) => {
  dispatch(managedGamesSlice.actions.loadingManagedGames());
  dispatch(
    watchManagedGamesActionBase({ userId }, async () => {
      log.info("Watching managed games for user: ", userId);
      return await Provider.watchManagedGames(userId, (games) => {
        log.info("Got updated managed games: ", games);
        dispatch(modelsUpdated(games, gameEntity));
      });
    })
  );
};

const stopWatchingManagedGamesAction = () => (dispatch) => {
  dispatch(stopWatchingManagedGamesActionBase({}));
};

/* --- Selectors ---- */
const selectManagedGamesState = (state) => state.managedGames;

// Adapter selectors are declared here (above the lifecycle selectors that build
// on them) so they exist when those createSelector inputs are evaluated.
export const {
  selectById: selectManagedGameById,
  selectIds: selectManagedGamesIds,
  selectEntities: selectManagedGameEntities,
  selectAll: selectAllManagedGames,
  selectTotal: selectTotalManagedGames,
} = managedGamesAdapter.getSelectors((state) => state.managedGames);

const selectManagedGamesLoading = createSelector(
  selectManagedGamesState,
  (s) => s.loadingGames
);

const selectSimpleGameStatus = (gameId) =>
  createSelector(
    (state) => selectManagedGameById(state, gameId),
    (game) => {
      return getSimpleGameStateForGame(game);
    }
  );

/*
 * Lifecycle-aware dashboard selectors (PRD §11). These power the sidebar
 * counts, filter tabs, hero slot, and the grid/list. Each memoizes on the full
 * managed-games array plus its scalar args, so they recompute only when the
 * Firestore listener pushes new games (or the filter/sort changes).
 */

// "all" / the default views exclude archived games; Archive is its own filter.
const isArchived = (game) =>
  getPublishStatus(game) === PUBLISH_STATUS.ARCHIVED;

/** Games whose lifecycle status matches `status` (treats missing as draft). */
const selectGamesByStatus = createSelector(
  [selectAllManagedGames, (_state, status) => status],
  (games, status) => games.filter((g) => getPublishStatus(g) === status)
);

/** Counts for the sidebar / filter-tab badges. `all` excludes archived. */
const selectGameCounts = createSelector([selectAllManagedGames], (games) => {
  const counts = {
    all: 0,
    drafts: 0,
    ready: 0,
    inProgress: 0,
    played: 0,
    archived: 0,
  };
  for (const game of games) {
    const status = getPublishStatus(game);
    if (status === PUBLISH_STATUS.ARCHIVED) {
      counts.archived += 1;
      continue;
    }
    counts.all += 1;
    if (status === PUBLISH_STATUS.DRAFT) counts.drafts += 1;
    else if (status === PUBLISH_STATUS.READY) counts.ready += 1;
    else if (status === PUBLISH_STATUS.IN_PROGRESS) counts.inProgress += 1;
    else if (status === PUBLISH_STATUS.PLAYED) counts.played += 1;
  }
  return counts;
});

/**
 * The single hero game (PRD §5.1): most-recently-touched game among
 * draft / ready / in-progress (never played, never archived). null when none.
 */
const HERO_STATUSES = new Set([
  PUBLISH_STATUS.DRAFT,
  PUBLISH_STATUS.READY,
  PUBLISH_STATUS.IN_PROGRESS,
]);

const selectHeroGame = createSelector([selectAllManagedGames], (games) => {
  const candidates = games.filter((g) => HERO_STATUSES.has(getPublishStatus(g)));
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => touchedAtMs(b) - touchedAtMs(a))[0];
});

const FILTER_PREDICATE = {
  all: (g) => !isArchived(g),
  drafts: (g) => getPublishStatus(g) === PUBLISH_STATUS.DRAFT,
  ready: (g) => getPublishStatus(g) === PUBLISH_STATUS.READY,
  "in-progress": (g) => getPublishStatus(g) === PUBLISH_STATUS.IN_PROGRESS,
  played: (g) => getPublishStatus(g) === PUBLISH_STATUS.PLAYED,
  archived: isArchived,
};

const timeOf = (value) => {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
};

const SORT_COMPARATOR = {
  touched: (a, b) => touchedAtMs(b) - touchedAtMs(a),
  edited: (a, b) =>
    timeOf(b.lastTouchedAt || b.createdAt) - timeOf(a.lastTouchedAt || a.createdAt),
  created: (a, b) => timeOf(b.createdAt) - timeOf(a.createdAt),
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
};

/**
 * Filtered + sorted games for the grid / queue view (PRD §7, §11). `filter` is
 * one of FILTER_PREDICATE's keys (default "all"); `sort` one of
 * SORT_COMPARATOR's (default "touched").
 */
const selectFilteredSortedGames = createSelector(
  [
    selectAllManagedGames,
    (_state, filter) => filter || "all",
    (_state, _filter, sort) => sort || "touched",
  ],
  (games, filter, sort) => {
    const predicate = FILTER_PREDICATE[filter] || FILTER_PREDICATE.all;
    const comparator = SORT_COMPARATOR[sort] || SORT_COMPARATOR.touched;
    return games.filter(predicate).sort(comparator);
  }
);

/* ---- Exports ---- */

export { selectManagedGamesLoading, selectSimpleGameStatus };

export {
  selectGamesByStatus,
  selectGameCounts,
  selectHeroGame,
  selectFilteredSortedGames,
};

export { watchManagedGamesAction, stopWatchingManagedGamesAction };

export default managedGamesSlice.reducer;
